/**
 * Offline Banner - shows when offline
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';

interface OfflineBannerProps {
  message?: string;
  position?: 'top' | 'bottom';
}

export function OfflineBanner({
  message,
  position = 'top',
}: OfflineBannerProps): JSX.Element | null {
  const { t } = useTranslation();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOffline) {
    return <></>;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        [position]: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        py: 1,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      <WifiOffIcon fontSize="small" />
      <Typography variant="body2" fontWeight="medium">
        {message || t('offlineBannerText')}
      </Typography>
    </Box>
  );
}

export default OfflineBanner;
